// Card KPI principali della dashboard
import { TrendingUp, TrendingDown, Ticket, Clock, AlertTriangle, CheckCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn } from '../../../lib/utils'
import { Skeleton } from '../../../components/ui/Skeleton'

interface KPI {
  label: string
  value: number
  delta?: number
  icon: React.ReactNode
  color: string
  bgColor: string
  href: string
}

interface KPICardsProps {
  data?: {
    open: number
    inProgress: number
    expired: number
    closedToday: number
    openDelta?: number
    inProgressDelta?: number
    expiredDelta?: number
    closedTodayDelta?: number
  }
  isLoading?: boolean
}

export function KPICards({ data, isLoading }: KPICardsProps) {
  const navigate = useNavigate()

  const kpis: KPI[] = [
    {
      label: 'Ticket aperti',
      value: data?.open ?? 0,
      delta: data?.openDelta,
      icon: <Ticket className="h-6 w-6" />,
      color: 'text-[var(--fc-primary)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)]',
      href: '/tickets?status=assigned',
    },
    {
      label: 'In lavorazione',
      value: data?.inProgress ?? 0,
      delta: data?.inProgressDelta,
      icon: <Clock className="h-6 w-6" />,
      color: 'text-[var(--fc-warning)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--fc-warning)_10%,transparent)]',
      href: '/tickets?status=in_progress',
    },
    {
      label: 'Scaduti SLA',
      value: data?.expired ?? 0,
      delta: data?.expiredDelta,
      icon: <AlertTriangle className="h-6 w-6" />,
      color: 'text-[var(--fc-danger)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--fc-danger)_10%,transparent)]',
      href: '/tickets?status=escalated',
    },
    {
      label: 'Chiusi oggi',
      value: data?.closedToday ?? 0,
      delta: data?.closedTodayDelta,
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'text-[var(--fc-success)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--fc-success)_10%,transparent)]',
      href: '/tickets?status=completed',
    },
  ]

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4">
            <Skeleton height={48} width={48} className="mb-3 rounded-lg" />
            <Skeleton height={14} className="mb-2 w-24" />
            <Skeleton height={28} className="w-16" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {kpis.map((kpi) => (
        <button
          key={kpi.label}
          type="button"
          onClick={() => navigate(kpi.href)}
          className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 shadow-[var(--fc-shadow-sm)] text-left cursor-pointer transition-shadow hover:shadow-[var(--fc-shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-primary)]"
          aria-label={`${kpi.label}: ${kpi.value} — vai ai ticket`}
        >
          <div className={cn('mb-3 flex h-12 w-12 items-center justify-center rounded-lg', kpi.bgColor)}>
            <span className={kpi.color} aria-hidden="true">{kpi.icon}</span>
          </div>
          <p className="text-sm text-[var(--fc-foreground-muted)]">{kpi.label}</p>
          <div className="mt-1 flex items-end gap-2">
            <span className="text-3xl font-bold text-[var(--fc-foreground)]">{kpi.value}</span>
            {kpi.delta !== undefined && (
              <span
                className={cn(
                  'mb-1 flex items-center gap-0.5 text-xs font-medium',
                  kpi.delta >= 0 ? 'text-[var(--fc-success)]' : 'text-[var(--fc-danger)]'
                )}
                aria-label={`Variazione: ${kpi.delta >= 0 ? '+' : ''}${kpi.delta}%`}
              >
                {kpi.delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(kpi.delta)}%
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  )
}
